<template>
  <div class="supplier-page">
    <!-- Header -->
    <header class="brand-gradient text-white border-bottom brand-border-4">
      <div class="container-7xl supplier-header">
        <!-- Gauche : logo + titre + sous-titre -->
        <div class="d-flex align-items-center header-left">
          <div class="logo-tile text-white mr-3">FNC</div>
          <div class="brand-stack">
            <div class="process-title">Process</div>
            <div class="process-subtitle">Gestion des Non-Conformités</div>
          </div>
        </div>
        <!-- Droite -->
        <div class="text-right">
          <small class="d-block text-slate-300">Portail Fournisseur</small>
          <small class="text-slate-400">EC2E - Confidentiel</small>
        </div>
      </div>
    </header>

    <!-- Main -->
    <main class="bg-page-gradient main-fill">
      <div class="container-7xl">
        <div class="card card-elevated w-100">
          <div class="card-body">
            <!-- État d'erreur FNC introuvable -->
            <div v-if="loadError" class="error-state">
              <h2 class="error-title">Lien invalide ou FNC introuvable</h2>
              <p class="error-text">
                {{ loadError }}
              </p>
            </div>

            <!-- Affichage normal -->
            <div v-else class="sheet-grid">
              <!-- Colonne gauche -->
              <section class="section-col">
                <div class="section-heading">
                  <h2 class="section-title mb-0">La non-conformité</h2>

                  <div class="d-flex align-items-center" v-if="!isLoading && fncData.fncNumber">
                    <span class="badge badge-pill badge-light mr-2">FNC</span>
                    <span class="fnc-number">{{ fncData.fncNumber }}</span>
                  </div>
                </div>

                <!-- Skeleton loader -->
                <div class="kv kv-2 kv-skeleton" v-if="isLoading">
                  <div
                      v-for="n in 14"
                      :key="'sk-' + n"
                      class="kv-item"
                      :class="{ 'kv-span2': n === 3 || n === 5 || n === 8 || n === 11 }"
                  >
                    <div class="skeleton skeleton-label mb-1"></div>
                    <div class="skeleton skeleton-value"></div>
                  </div>
                </div>

                <!-- Données réelles -->
                <div class="kv kv-2" v-else>
                  <div class="kv-item">
                    <div class="label-xs">Date de réception</div>
                    <div class="value-sm">{{ fncData.receptionDate }}</div>
                  </div>

                  <div class="kv-item">
                    <div class="label-xs">Lieu de réception</div>
                    <div class="value-sm">{{ fncData.receptionLocation }}</div>
                  </div>

                  <div class="kv-item">
                    <div class="label-xs">Origine</div>
                    <div class="value-sm d-flex align-items-center flex-wrap">
                      <span class="badge badge-pill badge-origin mr-2">{{ fncData.originType }}</span>
                      <span>{{ fncData.originDetail }}</span>
                    </div>
                  </div>

                  <div class="kv-item">
                    <div class="label-xs">Type de suivi</div>
                    <div class="value-sm">
                      <span
                          class="badge badge-pill"
                          :class="fncData.followUpType === 'MAJEURE' ? 'badge-majeure' : 'badge-mineure'"
                      >
                        {{ fncData.followUpType }}
                      </span>
                    </div>
                  </div>

                  <div class="kv-item kv-span2">
                    <div class="label-xs">Référence produit</div>
                    <div class="value-sm">{{ fncData.productReference }}</div>
                  </div>

                  <div class="kv-item">
                    <div class="label-xs">Quantité</div>
                    <div class="value-sm">{{ fncData.quantity }}</div>
                  </div>

                  <div class="kv-item">
                    <div class="label-xs">Fournisseur</div>
                    <div class="value-sm">{{ fncData.supplierNumber }}</div>
                  </div>

                  <div class="kv-item kv-span2">
                    <div class="label-xs">Constat de la non-conformité</div>
                    <div class="pill-area pill-danger">{{ fncData.cause }}</div>
                  </div>

                  <div class="kv-item kv-span2" v-if="fncData.internalObservations">
                    <div class="label-xs">Observations internes</div>
                    <div class="pill-area pill-neutral">{{ fncData.internalObservations }}</div>
                  </div>

                  <div class="kv-item">
                    <div class="label-xs">Responsable interne</div>
                    <div class="value-sm">{{ fncData.responsiblePerson }}</div>
                  </div>
                </div>
              </section>

              <!-- Séparateur vertical -->
              <div class="v-divider"></div>

              <!-- Colonne droite -->
              <section class="response-col">
                <h2 class="section-title text-orange">Votre réponse</h2>

                <!-- Bandeau si FNC archivée -->
                <div v-if="isArchived && !isLoading" class="alert alert-archived mb-3">
                  Cette non-conformité est <strong>clôturée</strong>.
                  Le formulaire n’est plus modifiable.
                </div>

                <!-- Skeleton côté formulaire -->
                <div v-if="isLoading" class="loader-right mb-3">
                  <div class="skeleton skeleton-label w-50 mb-3"></div>
                  <div class="skeleton skeleton-input mb-2"></div>
                  <div class="skeleton skeleton-input mb-4"></div>
                  <div class="skeleton skeleton-label w-75 mb-2"></div>
                  <div class="skeleton skeleton-textarea"></div>
                </div>

                <!-- Formulaire réel -->
                <div v-else class="response-fields">
                  <div class="form-grid">
                    <div class="response-field">
                      <label for="supplier-name">Nom et prénom <span class="text-danger">*</span></label>
                      <input id="supplier-name" type="text" class="form-control input-soft"
                             placeholder="Nom et prénom" v-model.trim="supplierData.supplierName" :disabled="isArchived">
                    </div>
                    <div class="response-field">
                      <label for="supplier-email">Email <span class="text-danger">*</span></label>
                      <input id="supplier-email" type="email" class="form-control input-soft"
                             placeholder="adresse@fournisseur.com" v-model.trim="supplierData.supplierEmail" :disabled="isArchived">
                      <small v-if="supplierData.supplierEmail && !isEmailValid" class="text-danger">Adresse email invalide.</small>
                    </div>
                  </div>

                  <div class="response-field">
                    <div class="field-heading">
                      <label for="supplier-cause">Cause <small>(facultatif)</small></label>
                      <small id="supplier-cause-remaining" class="field-counter">{{ causeRemaining }} caractères restants</small>
                    </div>
                    <textarea id="supplier-cause" v-model.trim="supplierData.cause" :maxlength="maxLen"
                              aria-describedby="supplier-cause-remaining"
                              :readonly="isArchived" rows="2" class="form-control input-soft cause-input"
                              placeholder="Expliquez l’origine de la non-conformité"></textarea>
                  </div>

                  <div class="response-field">
                    <div class="field-heading">
                      <label for="supplier-action">Action corrective <span v-if="!isMinor" class="text-danger">*</span><small v-else> (facultatif)</small></label>
                      <small class="field-counter">{{ remaining }} caractères restants</small>
                    </div>
                    <textarea id="supplier-action" class="form-control input-soft action-input" rows="3"
                              :maxlength="maxLen" v-model="supplierData.correctiveAction" :readonly="isArchived"
                              placeholder="Décrivez les actions prévues pour éviter la réapparition du défaut"></textarea>
                  </div>

                  <div class="form-grid date-fields">
                    <div class="response-field">
                      <label for="supplier-deadline">Délai de mise en place <span v-if="!isMinor" class="text-danger">*</span><small v-else> (facultatif)</small></label>
                      <input id="supplier-deadline" type="date" class="form-control input-soft"
                             v-model="supplierData.timeLimit" :disabled="isArchived">
                    </div>
                    <div class="response-field">
                      <label for="supplier-validation-date">Date de validation (par le fournisseur) <span class="text-danger">*</span></label>
                      <input id="supplier-validation-date" type="date" class="form-control input-soft"
                             v-model="supplierData.validationDate" :disabled="isArchived">
                    </div>
                  </div>
                </div>

                <div class="note-and-btn">
                  <p class="response-note">Votre réponse sera transmise au service Qualité. <span>* Champs obligatoires.</span></p>
                  <button type="button" class="btn btn-orange btn-send"
                          :disabled="isLoading || !isValid || isArchived" @click="handleSubmit">
                    <SendIcon :width="18" :height="18" />
                    <span>Envoyer ma réponse</span>
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </main>

    <footer class="supplier-footer brand-gradient text-white">
      <div class="container-7xl footer-content">
        <div class="footer-contact">
          <strong>Service Qualité EC2E</strong>
          <a href="mailto:qualite@ec2e.com">qualite@ec2e.com</a>
          <a href="tel:0185522014">01 85 52 20 14</a>
        </div>
        <details class="footer-information">
          <summary>Informations</summary>
          <div>Politique de confidentialité · Conditions d’utilisation · Mentions légales<br>EC2E · Process · Version 2.3.1</div>
        </details>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SupplierResponseForm',

  props: {
    uuid: {
      type: String,
      required: true
    }
  },

  components: {
    SendIcon: {
      functional: true,
      props: { width: { default: 16 }, height: { default: 16 }, class: { default: '' } },
      render(h, { props, data }) {
        return h('svg', {
          class: data.class,
          attrs: {
            xmlns: 'http://www.w3.org/2000/svg',
            width: props.width,
            height: props.height,
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
          }
        }, [
          h('line', { attrs: { x1: '22', y1: '2', x2: '11', y2: '13' } }),
          h('polygon', { attrs: { points: '22 2 15 22 11 13 2 9 22 2' } })
        ]);
      }
    }
  },

  data() {
    return {
      maxLen: 1000,
      isLoading: true,
      isArchived: false,
      loadError: null,

      fncData: {
        fncNumber: '',
        receptionDate: '',
        receptionLocation: '',
        declaredBy: '',
        originType: '',
        originDetail: '',
        productReference: '',
        quantity: '',
        supplierNumber: '',
        contact: '',
        cause: '',
        internalObservations: '',
        followUpType: '',
        responsiblePerson: '',
      },

      supplierData: {
        supplierName: '',
        supplierEmail: '',
        cause: '',
        validationDate: '',
        correctiveAction: '',
        timeLimit: '',
        time_limit: '',
      },
    };
  },

  computed: {
    isMinor() {
      return this.fncData.followUpType === 'MINEURE';
    },
    remaining() {
      return Math.max(this.maxLen - (this.supplierData.correctiveAction || '').length, 0);
    },
    causeRemaining() {
      return Math.max(this.maxLen - (this.supplierData.cause || '').length, 0);
    },
    isEmailValid() {
      const email = this.supplierData.supplierEmail || '';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    isValid() {
      return (this.supplierData.supplierName || '').trim().length > 0 &&
          this.isEmailValid &&
          !!this.supplierData.validationDate &&
          (this.isMinor || ((this.supplierData.correctiveAction || '').trim().length > 0 &&
          !!this.supplierData.timeLimit));
    }
  },

  created() {
    this.fetchFncFromUuid();
  },

  methods: {
    async fetchFncFromUuid() {
      this.isLoading = true;
      this.loadError = null;
      this.isArchived = false;
      this.supplierData.timeLimit = '';

      try {
        const response = await axios.get(
            process.env.VUE_APP_API_BASE_URL + 'fnc/extern/' + this.uuid
        );
        const fnc = response.data;

        if (!fnc || !fnc.num) {
          this.loadError = "Aucune non-conformité n'a été trouvée pour ce lien. Merci de vérifier l'URL ou de contacter votre interlocuteur EC2E.";
          return;
        }

        const formatDate = function (dateStr) {
          if (!dateStr) return '';
          const d = new Date(dateStr.replace(' ', 'T'));
          return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('fr-FR');
        };

        this.isArchived = !!fnc.archived_at;

        this.fncData = {
          fncNumber: fnc.num || '',
          receptionDate: formatDate(fnc.received_at),
          receptionLocation: fnc.location || '',
          declaredBy:
              (fnc.detector && fnc.detector.name) ||
              (fnc.creator && fnc.creator.name) ||
              '',
          originType: fnc.origin || '',
          originDetail: fnc.supplier ? fnc.supplier.name : '',
          productReference: fnc.product
              ? fnc.product.reference + ' - ' + fnc.product.name
              : '',
          quantity: (fnc.quantity !== null && fnc.quantity !== undefined)
              ? fnc.quantity
              : '',
          supplierNumber: fnc.supplier
              ? fnc.supplier.num + ' ' + fnc.supplier.name
              : '',
          contact: '',
          cause: fnc.cause || '',
          internalObservations: fnc.intern_observation || '',
          followUpType: (fnc.importance || '').toUpperCase(),
          responsiblePerson: fnc.controller ? fnc.controller.name : '',
        };

        this.supplierData = {
          supplierName: fnc.supplier_fullname || '',
          supplierEmail: fnc.supplier_email || '',
          cause: fnc.supplier_cause || '',
          validationDate: this.formatDateForInput(fnc.supplier_validation_date),
          correctiveAction: fnc.corrective_action || '',
          timeLimit: this.formatDateForInput(fnc.time_limit),
          time_limit: this.formatDateForInput(fnc.time_limit),
        };
      } catch (e) {
        console.error('Erreur lors du chargement de la FNC externe :', e);

        if (e.response && e.response.status === 404) {
          this.loadError = "Aucune non-conformité n'a été trouvée pour ce lien. Merci de vérifier l'URL ou de contacter votre interlocuteur EC2E.";
        } else {
          this.loadError = "Une erreur est survenue lors du chargement de la non-conformité. Merci de réessayer plus tard ou de contacter votre interlocuteur EC2E.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    formatDateForInput(value) {
      if (!value) return null;

      // Si c'est déjà un objet Date
      if (value instanceof Date) {
        return value.toISOString().slice(0, 10); // "YYYY-MM-DD"
      }

      // Si c'est une string venant du backend : "2025-11-21 00:00:00" ou "2025-11-21T00:00:00.000000Z"
      return value.toString().slice(0, 10);
    },
    async handleSubmit() {
      if (this.isArchived || this.isLoading || !this.isValid) {
        return;
      }

      // Empêche double-submit
      this.isLoading = true;

      const payload = {
        fnc: this.fncData,
        supplier: {
          ...this.supplierData,
          time_limit: this.supplierData.timeLimit
        }
      };

      try {
        // 👉 Appel POST vers ton endpoint API
        const response = await axios.post(
            process.env.VUE_APP_API_BASE_URL + 'fnc/extern/' + this.uuid + '/response',
            payload
        );

        console.log('Réponse API :', response.data);

        alert('Votre réponse a bien été envoyée. Merci.');

        this.$emit('submitted', response.data);

      } catch (error) {
        console.error('Erreur lors de l’envoi de la réponse fournisseur :', error);

        let msg = "Une erreur est survenue lors de l’envoi de votre réponse.";

        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }

        alert(msg);

      } finally {
        this.isLoading = false;
      }
    }

  }
};
</script>

<style scoped>
.supplier-page { min-height:100vh; min-height:100dvh; display:flex; flex-direction:column; color:#1e293b; }
.supplier-page * { box-sizing:border-box; }
.text-orange { color:#c75318 !important; }
.text-slate-300 { color:#cbd5e1 !important; }
.text-slate-400 { color:#94a3b8 !important; }
.brand-gradient { background:linear-gradient(90deg,#0f172a,#111827); }
.brand-border-4 { border-bottom:3px solid #f97316 !important; }
.bg-page-gradient { background:linear-gradient(135deg,#f8fafc,#e2e8f0); }
.container-7xl { width:100%; max-width:1320px; margin:0 auto; padding:0 clamp(16px,2.5vw,32px); }
.supplier-header { display:flex; align-items:center; justify-content:space-between; padding-top:10px; padding-bottom:10px; }
.brand-stack { line-height:1.2; }
.process-title { color:#fff; font-weight:700; font-size:21px; }
.process-subtitle { color:#cbd5e1; font-size:12px; }
.logo-tile { width:38px; height:38px; background:#f97316; border-radius:10px; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:13px; }
.main-fill { flex:1; display:flex; align-items:center; padding:16px 0; }
.card-elevated { margin:0; border:1px solid #e2e8f0; border-radius:14px; background:#fff; box-shadow:0 12px 35px -20px #64748b; }
.card-elevated > .card-body { padding:24px; }
.sheet-grid { display:grid; grid-template-columns:minmax(0,.9fr) 1px minmax(0,1.1fr); gap:24px; align-items:start; }
.section-col,.response-col { min-width:0; }
.section-heading { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:8px; margin-bottom:18px; }
.section-title { font-size:17px; line-height:1.3; font-weight:600; margin:0; }
.response-col > .section-title { margin-bottom:18px; }
.fnc-number { font-size:12px; font-weight:600; color:#334155; white-space:nowrap; }
.v-divider { background:#e2e8f0; align-self:stretch; }
.kv-2 { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:12px 18px; }
.kv-item { min-width:0; }
.kv-span2 { grid-column:1 / -1; }
.label-xs { font-size:10px; line-height:1.4; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.035em; margin-bottom:3px; }
.value-sm { font-size:13px; line-height:1.4; overflow-wrap:anywhere; }
.badge-origin { background:#ffedd5; color:#9a3412; }
.badge-majeure { background:#fee2e2; color:#991b1b; }
.badge-mineure { background:#fef3c7; color:#92400e; }
.badge-light { background:#e5e7eb; color:#374151; }
.badge-pill { padding:3px 7px; border-radius:999px; font-size:10px; font-weight:600; line-height:1.2; white-space:normal; }
.pill-area { border-radius:7px; padding:8px 10px; border:1px solid transparent; font-size:13px; line-height:1.4; white-space:pre-wrap; overflow-wrap:anywhere; }
.pill-danger { background:#fff5f5; border-color:#fecaca; }
.pill-neutral { background:#f8fafc; border-color:#e2e8f0; }
.response-fields { display:grid; gap:12px; }
.form-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:12px; }
.response-field { min-width:0; display:flex; flex-direction:column; }
.response-field label { display:block; margin:0 0 5px; color:#334155; font-size:12px; font-weight:600; line-height:1.35; }
.response-field label small { font-size:11px; font-weight:400; color:#64748b; }
.field-heading { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:baseline; gap:0 8px; }
.field-counter { font-size:10px; line-height:1.4; color:#64748b; margin-bottom:5px; }
.input-soft { width:100%; min-width:0; background:#f8fafc; border:1px solid #cbd5e1; border-radius:7px; font-size:13px; line-height:1.4; padding:8px 10px; color:#1e293b; }
input.input-soft { height:36px; }
.input-soft:focus { background:#fff; border-color:#f97316; box-shadow:0 0 0 3px #ffedd5; }
.input-soft::placeholder { color:#798697; opacity:1; }
textarea.input-soft { resize:vertical; max-height:none; }
.cause-input { height:64px; min-height:64px; }
.action-input { height:86px; min-height:86px; }
.date-fields label { flex:1; }
.note-and-btn { margin-top:14px; border-top:1px solid #e2e8f0; padding-top:12px; display:flex; flex-wrap:wrap; align-items:center; gap:10px 14px; }
.response-note { flex:1 1 180px; margin:0; color:#64748b; font-size:11px; line-height:1.5; }
.response-note span { display:block; }
.btn-send { flex:0 0 auto; display:flex; align-items:center; justify-content:center; gap:8px; margin:0; min-height:40px; padding:10px 14px; border-radius:8px; font-size:12px; text-transform:none; }
.btn-orange { background:#e96524; border-color:#e96524; color:#fff; font-weight:600; }
.btn-orange:hover { background:#c75318; border-color:#c75318; }
.btn-orange:disabled { background:#e2e8f0; border-color:#e2e8f0; color:#64748b; opacity:1; cursor:not-allowed; }
.supplier-footer { flex-shrink:0; }
.footer-content { display:flex; align-items:center; justify-content:space-between; gap:10px 20px; padding-top:10px; padding-bottom:10px; font-size:11px; line-height:1.5; }
.footer-contact { display:flex; flex-wrap:wrap; gap:4px 16px; }
.footer-contact strong { font-weight:600; color:#cbd5e1; }
.footer-contact a { color:#cbd5e1; }
.footer-information { color:#94a3b8; }
.footer-information summary { cursor:pointer; white-space:nowrap; }
.footer-information div { max-width:330px; margin-top:6px; }
.alert-archived { background:#fef3c7; border:1px solid #facc15; color:#92400e; border-radius:8px; padding:8px 10px; font-size:12px; }
.error-state { min-height:260px; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; }
.error-title { font-size:18px; font-weight:600; }
.error-text { max-width:520px; font-size:14px; }
.skeleton { position:relative; overflow:hidden; background:#e5e7eb; border-radius:6px; }
.skeleton::after { content:''; position:absolute; inset:0; transform:translateX(-100%); background:linear-gradient(90deg,transparent,#ffffff99,transparent); animation:skeleton-loading 1.2s infinite; }
.skeleton-label { height:10px; width:70%; }
.skeleton-value { height:18px; width:100%; }
.skeleton-input { height:36px; }
.skeleton-textarea { height:150px; }
@keyframes skeleton-loading { 100% { transform:translateX(100%); } }
@media (min-width:760px) and (max-height:720px) {
  .main-fill { padding:12px 0; }
  .card-elevated > .card-body { padding:18px 22px; }
  .section-heading,.response-col > .section-title { margin-bottom:12px; }
  .response-fields { gap:10px; }
  .kv-2 { row-gap:10px; }
  .note-and-btn { margin-top:10px; padding-top:10px; }
}
@media (min-width:760px) and (max-width:1050px) {
  .sheet-grid { gap:18px; grid-template-columns:minmax(0,.85fr) 1px minmax(0,1.15fr); }
  .card-elevated > .card-body { padding:18px; }
  .kv-2 { column-gap:12px; }
  .section-title { font-size:15px; }
  .note-and-btn { flex-direction:column; align-items:stretch; }
  .response-note { flex:auto; }
  .response-note span { display:inline; }
}
@media (max-width:759.98px) {
  .supplier-header { padding-top:12px; padding-bottom:12px; }
  .process-subtitle { font-size:10px; }
  .main-fill { align-items:flex-start; }
  .card-elevated > .card-body { padding:18px; }
  .sheet-grid { grid-template-columns:minmax(0,1fr); gap:22px; }
  .v-divider { display:none; }
  .section-heading { margin-bottom:14px; }
  .footer-content { flex-wrap:wrap; }
}
@media (max-width:420px) {
  .form-grid { grid-template-columns:minmax(0,1fr); }
  .note-and-btn { align-items:stretch; flex-direction:column; }
  .response-note { flex:auto; }
  .supplier-header > .text-right { font-size:11px; }
  .container-7xl { padding-left:12px; padding-right:12px; }
}
</style>
